import React, { useCallback, useEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useFeatures } from '@atc/features';

import {
    Button,
    DisclaimerText,
    Link,
    PageSection,
    Text,
} from 'reaxl';
import { sendClick, sendImpressions } from 'reaxl-analytics';

import importContextSearchUtilityBundle from '@/utilities/importContextSearchUtilityBundle';

import remoteActions from '@/actions/vdp/remoteActions';

import {
    fraudReportDuck,
    vdpResultsDuck,
} from '@/ducks/vdp';

import FraudReport from '@/components/FraudReport/index.js';

export function FraudReportContainer() {
    const dispatch = useDispatch();

    const {
        brand: [, { visitor_agreement_link: visitorAgreementLink }],
        fraud_report: [isFraudReportEnabled, { fraud_awareness_tips: fraudTips }],
        contextual_search_chatbot: [isChatbotEnabled],
    } = useFeatures(['brand', 'fraud_report', 'contextual_search_chatbot']);

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const fraudReport = useSelector(fraudReportDuck.selectors.getDuckState);
    const { serverError, showConfirmation } = fraudReport;
    const triggerTextRef = useRef();

    useEffect(() => {
        if (isChatbotEnabled) {
            sendImpressions({ name: 'chatbotImpression', data: { siteArea: 'fraud' } });

            const setupChatbotEvent = async () => {
                const { on } = await importContextSearchUtilityBundle();
                on('ctxsrch.fraud.modal.open', () => {
                    document.querySelector('cai-context-search-side-bar').close();
                    triggerTextRef.current.click();
                });
            };
            setupChatbotEvent();
        }
    }, [isChatbotEnabled]);

    const isSidebarLoaded = useRef(false);
    const sidebarComponent = useRef(null);
    const showContextSearch = useCallback(async (e) => {
        if (!isSidebarLoaded.current) {
            isSidebarLoaded.current = true;
            sendClick('chatbotCTAClick', e, { linkText: 'ai-powered-chatbot' });

            if (!sidebarComponent.current) {
                const { showContextSearchSideBar } = await importContextSearchUtilityBundle();
                sidebarComponent.current = await showContextSearchSideBar({
                    question: 'I want to learn more about fraud awareness',
                    utterancePillOne: 'I think this listing seems fraudulent',
                    utterancePillTwo: 'Who can I contact about fraud?',
                    utterancePillThree: 'How do I file a complaint?',
                });
            } else {
                sidebarComponent.current.open();
            }
            isSidebarLoaded.current = false;
        }
    }, []);

    const handleSendFraud = useCallback((data) => {
        dispatch(remoteActions.sendFraud(data));
    }, [dispatch]);

    const handleResetFraud = useCallback(() => {
        dispatch(fraudReportDuck.creators.reset());
    }, [dispatch]);

    return (
        <PageSection
            alignment="left"
            className="col-lg-8"
            uiContext="section-3"
            subheading="More About Vehicle Details & Information"
        >
            <DisclaimerText componentClass="p">
                This page contains information provided by Chrome Data with additional vehicle details and images from the seller and may include data from third parties and/or machine learning. Sellers are required to submit information that is true and correct. We have not independently verified the information or details about any vehicles for sale. Please see the
                <Link
                    className="margin-horizontal-1"
                    label="Visitor Agreement"
                    target="_blank"
                    href={visitorAgreementLink}
                />
                for more information about vehicle data.
            </DisclaimerText>
            {isFraudReportEnabled && (
                <div>
                    <DisclaimerText>
                        <Text
                            className="margin-right-1"
                            size={300}
                            weight="bold"
                            componentClass="strong"
                        >
                            Suspect fraud?
                        </Text>
                        Is the vehicle information inaccurate?
                        <FraudReport
                            className="margin-left-1"
                            listing={listing}
                            onSubmit={handleSendFraud}
                            onClose={handleResetFraud}
                            serverError={serverError}
                            showConfirmation={showConfirmation}
                            triggerTextRef={triggerTextRef}
                        />
                        .
                    </DisclaimerText>
                    {isChatbotEnabled ? (
                        <DisclaimerText>
                            Have questions about Fraud? Awareness is the best defense - use our
                            <Button
                                bsStyle="text-decorated"
                                className="margin-horizontal-1"
                                onClick={showContextSearch}
                            >
                                AI-powered chatbot
                            </Button>
                            to ask questions and help protect yourself against fraud
                        </DisclaimerText>
                    ) : (
                        <DisclaimerText>
                            Awareness is the best defense.
                            <Link
                                className="margin-horizontal-1"
                                label="Please read our fraud awareness tips"
                                target="_blank"
                                rel="noopener"
                                href={fraudTips}
                            />
                            for things you can do to help protect yourself against fraud.
                        </DisclaimerText>
                    )}
                </div>
            )}
        </PageSection>
    );
}
export default FraudReportContainer;
